"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { css } from "styled-system/css";
import { addMonths } from "date-fns";
import { formatInTimeZone, fromZonedTime } from "date-fns-tz";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";
import { useConfirm } from "@/hooks/useConfirm";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { BookCalendarGrid } from "@/components/book/book-calendar-grid";
import { GOOGLE_EVENT_COLORS, googleEventColorHex } from "@/lib/google-calendar-colors";
import { overlapConfirmMessage, type ConflictInfo } from "./overbook";
import { ClientSearchAutocomplete, useClientList, type ClientRow } from "./client-search";

type ServiceOpt = {
  id: string;
  kind: "service" | "court";
  name: string;
  duration: number;
  durationOptions?: number[];
  price: number | null;
  requiresDeposit: boolean;
};

export type AppointmentClientPrefill = {
  clientName: string;
  clientEmail: string;
  clientPhone: string;
};

export type EditAppointmentData = {
  id: string;
  serviceId: string | null;
  courtId?: string | null;
  startTime: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  notes: string | null;
  staffMemberId?: string | null;
  /** Título original del evento de Google, capturado al convertirlo en turno. Editable solo desde acá. */
  originalGoogleTitle?: string | null;
};

const ARG_TZ = "America/Argentina/Buenos_Aires";
const PRIMARY = "#4f46e5";

function isEmailValid(email: string): boolean {
  if (!email.trim()) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

function isPhoneValid(phone: string): boolean {
  if (!phone.trim()) return true;
  return phone.replace(/\D/g, "").length >= 7;
}

export function NewAppointmentModal({
  open,
  onClose,
  initialStartIso,
  onCreated,
  onEdited,
  clientPrefill = null,
  onClientPrefillConsumed,
  editAppointment = null,
  timeZone,
}: {
  open: boolean;
  onClose: () => void;
  initialStartIso: string | null;
  initialEndIso?: string | null;
  onCreated: () => void;
  onEdited?: () => void;
  clientPrefill?: AppointmentClientPrefill | null;
  onClientPrefillConsumed?: () => void;
  editAppointment?: EditAppointmentData | null;
  /** Zona horaria IANA del profesional. Toda conversión hora local ↔ UTC usa esta zona, no la del navegador. */
  timeZone?: string;
}) {
  const { refresh } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const navigate = useNavigate();
  const tz = timeZone ?? ARG_TZ;

  // --- service / schedule state ---
  const [services, setServices] = useState<ServiceOpt[]>([]);
  const [serviceId, setServiceId] = useState<string>("");
  const [dateStr, setDateStr] = useState<string>("");
  const [slotIso, setSlotIso] = useState<string | null>(null);
  const [calendarMonth, setCalendarMonth] = useState<string>(() =>
    formatInTimeZone(new Date(), tz, "yyyy-MM"),
  );
  const [availableDates, setAvailableDates] = useState<Set<string>>(new Set());
  const [slots, setSlots] = useState<{ iso: string; label: string }[]>([]);
  const [loadingMonth, setLoadingMonth] = useState(false);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [customDurationMin, setCustomDurationMin] = useState<number | "">("");
  const [customTime, setCustomTime] = useState<string>("");

  // --- staff ---
  const [staffOptions, setStaffOptions] = useState<{ id: string; name: string; googleColorId: string; googleCalendarBgColor: string | null; googleCalendarId: string | null }[]>([]);
  const [staffMemberId, setStaffMemberId] = useState("");

  // --- client fields ---
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [originalGoogleTitle, setOriginalGoogleTitle] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // --- client autocomplete ---
  const clients = useClientList(open);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);

  // --- flow ---
  const [step, setStep] = useState<"form" | "confirm">("form");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const minDate = useMemo(() => formatInTimeZone(new Date(), tz, "yyyy-MM-dd"), [tz]);
  const minCalendarMonth = minDate.slice(0, 7);
  const maxCalendarMonth = useMemo(
    () =>
      formatInTimeZone(
        addMonths(fromZonedTime(`${minCalendarMonth}-01T12:00:00`, tz), 12),
        tz,
        "yyyy-MM",
      ),
    [minCalendarMonth, tz],
  );
  const canPrevMonth = calendarMonth > minCalendarMonth;
  const canNextMonth = calendarMonth < maxCalendarMonth;

  // --- open / reset effect ---
  useEffect(() => {
    if (!open) return;
    setMsg(null);
    setStep("form");
    setSelectedClientId(null);
    setEmailError(null);
    setPhoneError(null);
    setSlotIso(null);
    setSlots([]);
    setCustomDurationMin("");

    if (editAppointment) {
      setClientName(editAppointment.clientName);
      setClientEmail(editAppointment.clientEmail ?? "");
      setClientPhone(editAppointment.clientPhone ?? "");
      setNotes(editAppointment.notes ?? "");
      setOriginalGoogleTitle(editAppointment.originalGoogleTitle ?? null);
      setServiceId(editAppointment.serviceId ?? editAppointment.courtId ?? "");
      const dt = formatInTimeZone(editAppointment.startTime, tz, "yyyy-MM-dd");
      setDateStr(dt);
      setCalendarMonth(dt.slice(0, 7));
      setCustomTime(formatInTimeZone(editAppointment.startTime, tz, "HH:mm"));
    } else {
      setClientName("");
      setClientEmail("");
      setClientPhone("");
      setNotes("");
      setOriginalGoogleTitle(null);
      setServiceId("");
      setCustomTime("");
      if (initialStartIso) {
        setDateStr(formatInTimeZone(initialStartIso, tz, "yyyy-MM-dd"));
        setCalendarMonth(formatInTimeZone(initialStartIso, tz, "yyyy-MM"));
      } else {
        setDateStr("");
        setCalendarMonth(formatInTimeZone(new Date(), tz, "yyyy-MM"));
      }
    }

    void (async () => {
      const [rSvc, rCourts, rStaff] = await Promise.all([
        fetchWithSessionSync("/api/dashboard/services", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/courts", { credentials: "include" }, refresh),
        fetchWithSessionSync("/api/dashboard/agenda-staff", { credentials: "include" }, refresh),
      ]);
      const list: ServiceOpt[] = [];
      if (rSvc.ok) {
        const svcList = (await rSvc.json()) as Omit<ServiceOpt, "kind">[];
        list.push(...svcList.map((s) => ({ ...s, kind: "service" as const })));
      }
      if (rCourts.ok) {
        const d = (await rCourts.json()) as {
          courts: { id: string; name: string; durationMinutes: number; pricePerSlot: number | null }[];
        };
        list.push(
          ...d.courts.map((c) => ({
            id: c.id,
            kind: "court" as const,
            name: c.name,
            duration: c.durationMinutes,
            price: c.pricePerSlot,
            requiresDeposit: false,
          })),
        );
      }
      setServices(list);
      if (editAppointment) {
        setServiceId(editAppointment.serviceId ?? editAppointment.courtId ?? "");
      } else {
        setServiceId((curr) => curr || list[0]?.id || "");
      }
      if (rStaff.ok) {
        const d = (await rStaff.json()) as { staff: { id: string; name: string; googleColorId: string; isActive: boolean; googleCalendarBgColor: string | null; googleCalendarId: string | null }[] };
        const active = d.staff.filter((s) => s.isActive);
        setStaffOptions(active);
        if (editAppointment) {
          setStaffMemberId(editAppointment.staffMemberId ?? "");
        } else {
          setStaffMemberId(active.length === 1 ? active[0].id : "");
        }
      }
    })();
  }, [open, initialStartIso, editAppointment, refresh]);

  // --- client prefill effect ---
  useEffect(() => {
    if (!open || !clientPrefill?.clientEmail) return;
    setClientName(clientPrefill.clientName);
    setClientEmail(clientPrefill.clientEmail);
    setClientPhone(clientPrefill.clientPhone);
    onClientPrefillConsumed?.();
  }, [open, clientPrefill, onClientPrefillConsumed]);

  // --- derived ---
  const selected = useMemo(() => services.find((s) => s.id === serviceId) ?? null, [services, serviceId]);

  const customTimeValid = useMemo(() => {
    if (!customTime) return false;
    const m = /^(\d{2}):(\d{2})$/.exec(customTime);
    if (!m) return false;
    const h = parseInt(m[1], 10);
    const min = parseInt(m[2], 10);
    return h >= 0 && h <= 23 && min >= 0 && min <= 59;
  }, [customTime]);

  const resolvedStartIso = useMemo(() => {
    if (customTime && customTimeValid && dateStr) {
      return fromZonedTime(`${dateStr}T${customTime}:00`, tz).toISOString();
    }
    return slotIso;
  }, [customTime, customTimeValid, dateStr, slotIso, tz]);

  const effectiveDuration =
    selected?.kind !== "court" && customDurationMin !== "" && Number(customDurationMin) >= 5
      ? Number(customDurationMin)
      : (selected?.duration ?? 30);

  const confirmDateLabel = useMemo(
    () => (resolvedStartIso ? formatInTimeZone(resolvedStartIso, tz, "dd/MM/yyyy 'a las' HH:mm") : "—"),
    [resolvedStartIso, tz],
  );

  // --- availability-month ---
  useEffect(() => {
    if (!open || !serviceId) return;
    let cancelled = false;
    setLoadingMonth(true);
    const u = new URL("/api/dashboard/availability-month", window.location.origin);
    if (selected?.kind === "court") u.searchParams.set("courtId", serviceId);
    else u.searchParams.set("serviceId", serviceId);
    u.searchParams.set("month", calendarMonth);
    if (editAppointment?.id) u.searchParams.set("excludeAppointmentId", editAppointment.id);
    void fetchWithSessionSync(u.pathname + u.search, { credentials: "include" }, refresh)
      .then(async (res) => {
        const data = (await res.json().catch(() => ({}))) as { error?: string; dates?: string[] };
        if (!res.ok) throw new Error(data.error ?? "Error");
        return data.dates ?? [];
      })
      .then((dates) => {
        if (!cancelled) setAvailableDates(new Set(dates));
      })
      .catch(() => {
        if (!cancelled) setAvailableDates(new Set());
      })
      .finally(() => {
        if (!cancelled) setLoadingMonth(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, serviceId, calendarMonth, editAppointment?.id, refresh, selected?.kind]);

  // --- slots ---
  const loadSlots = useCallback(async () => {
    if (!serviceId || !dateStr) return;
    setLoadingSlots(true);
    try {
      const u = new URL("/api/dashboard/availability", window.location.origin);
      if (selected?.kind === "court") u.searchParams.set("courtId", serviceId);
      else u.searchParams.set("serviceId", serviceId);
      u.searchParams.set("date", dateStr);
      if (editAppointment?.id) u.searchParams.set("excludeAppointmentId", editAppointment.id);
      const res = await fetchWithSessionSync(u.pathname + u.search, { credentials: "include" }, refresh);
      const data = (await res.json().catch(() => ({}))) as { slots?: { iso: string; label: string }[] };
      if (!res.ok) throw new Error("Error");
      setSlots(data.slots ?? []);
      setSlotIso(null);
    } catch {
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  }, [serviceId, dateStr, editAppointment?.id, refresh, selected?.kind]);

  useEffect(() => {
    if (!open) return;
    if (!dateStr) {
      setSlots([]);
      setSlotIso(null);
      return;
    }
    void loadSlots();
  }, [open, dateStr, loadSlots]);

  const goPrevMonth = useCallback(() => {
    setCalendarMonth((m) => {
      const prev = formatInTimeZone(addMonths(fromZonedTime(`${m}-01T12:00:00`, tz), -1), tz, "yyyy-MM");
      return prev < minCalendarMonth ? m : prev;
    });
  }, [minCalendarMonth]);

  const goNextMonth = useCallback(() => {
    setCalendarMonth((m) => {
      const next = formatInTimeZone(addMonths(fromZonedTime(`${m}-01T12:00:00`, tz), 1), tz, "yyyy-MM");
      return next > maxCalendarMonth ? m : next;
    });
  }, [maxCalendarMonth]);

  // --- client autocomplete helpers ---
  function selectClient(c: ClientRow) {
    setSelectedClientId(c.clientId);
    setClientName(c.displayName);
    setClientEmail(c.email ?? "");
    setClientPhone(c.phone ?? "");
    setEmailError(null);
    setPhoneError(null);
  }

  function clearClientSelection() {
    setSelectedClientId(null);
    setClientName("");
    setClientEmail("");
    setClientPhone("");
  }

  // --- validation ---
  function validateClientFields(): boolean {
    let valid = true;
    if (!isEmailValid(clientEmail)) {
      setEmailError("Formato de email inválido");
      valid = false;
    } else {
      setEmailError(null);
    }
    if (!isPhoneValid(clientPhone)) {
      setPhoneError("El teléfono debe tener al menos 7 dígitos");
      valid = false;
    } else {
      setPhoneError(null);
    }
    return valid;
  }

  const canProceedToConfirm =
    !busy &&
    Boolean(resolvedStartIso) &&
    Boolean(serviceId) &&
    Boolean(clientName.trim()) &&
    (Boolean(clientEmail.trim()) || Boolean(clientPhone.trim())) &&
    isEmailValid(clientEmail) &&
    isPhoneValid(clientPhone);

  function handleProceedToConfirm() {
    if (!validateClientFields()) return;
    if (!canProceedToConfirm) return;
    setStep("confirm");
  }

  /**
   * Sanity-check de desfase horario (control preventivo). La hora que el usuario ingresó debe coincidir
   * con la hora que se va a guardar/sincronizar (wall-clock en la zona del profesional). Con el manejo
   * de timezone corregido esto siempre coincide; si no, avisamos en vez de guardar un horario incorrecto.
   */
  function detectTimeDrift(startIso: string): { entered: string; willSync: string } | null {
    const willSync = formatInTimeZone(startIso, tz, "HH:mm");
    let entered: string | null = null;
    if (customTime && customTimeValid) entered = customTime;
    else if (slotIso) entered = slots.find((s) => s.iso === slotIso)?.label ?? null;
    if (entered && entered !== willSync) return { entered, willSync };
    return null;
  }

  // --- submit ---
  async function submit() {
    if (!serviceId || !resolvedStartIso) return;

    // UX #1: control de sanity-check de desfase horario antes de enviar.
    const drift = detectTimeDrift(resolvedStartIso);
    if (drift) {
      const proceed = await confirm(
        `Atención: la hora ingresada (${drift.entered}) no coincide con la hora que se guardaría (${drift.willSync}).\n\n` +
          `Esto puede indicar un problema de zona horaria. ¿Querés continuar de todos modos?`,
      );
      if (!proceed) return;
    }

    setBusy(true);
    setMsg(null);
    const endTimeIso = new Date(new Date(resolvedStartIso).getTime() + effectiveDuration * 60000).toISOString();
    const basePayload = {
      startTimeIso: resolvedStartIso,
      endTimeIso,
      ...(selected?.kind === "court" ? { courtId: serviceId } : { serviceId }),
      clientName,
      clientEmail: clientEmail.trim() || undefined,
      clientPhone: clientPhone.trim() || null,
      notes: notes.trim() || null,
      staffMemberId: staffMemberId || null,
      ...(editAppointment ? { originalGoogleTitle: originalGoogleTitle?.trim() || null } : {}),
    };

    if (editAppointment) {
      const sendPatch = (allowOverlap: boolean) =>
        fetchWithSessionSync(
          `/api/dashboard/appointments/${editAppointment.id}`,
          { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...basePayload, force: true, allowOverlap }) },
          refresh,
        );
      let r = await sendPatch(false);
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string; code?: string; conflict?: ConflictInfo };
        if (j.code === "OVERLAP" && j.conflict && (await confirm(overlapConfirmMessage(j.conflict, tz)))) {
          r = await sendPatch(true);
          if (!r.ok) {
            const j2 = (await r.json().catch(() => ({}))) as { error?: string };
            setBusy(false);
            setMsg(j2.error ?? "No se pudo actualizar");
            setStep("form");
            return;
          }
          toast.success("Sobreturno guardado.");
        } else {
          setBusy(false);
          setMsg(j.error ?? "No se pudo actualizar");
          setStep("form");
          return;
        }
      }
      // UX #3: avisar si falló la sincronización con Google (el cambio igual se guardó).
      const data = (await r.json().catch(() => ({}))) as { googleSync?: { ok: boolean } };
      if (data.googleSync && !data.googleSync.ok) {
        toast.error("El turno se actualizó, pero no se pudo sincronizar con Google Calendar.");
      }
      setBusy(false);
      onEdited?.();
      onClose();
    } else {
      const sendPost = (allowOverlap: boolean) =>
        fetchWithSessionSync(
          "/api/dashboard/appointments",
          { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...basePayload, allowOverlap }) },
          refresh,
        );
      let r = await sendPost(false);
      let isOverbook = false;
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string; code?: string; conflict?: ConflictInfo };
        // UX #2: sobreturno con confirmación explícita y detalle del turno solapado.
        if (j.code === "OVERLAP" && j.conflict && (await confirm(overlapConfirmMessage(j.conflict, tz)))) {
          r = await sendPost(true);
          isOverbook = true;
          if (!r.ok) {
            const j2 = (await r.json().catch(() => ({}))) as { error?: string };
            setBusy(false);
            setMsg(j2.error ?? "No se pudo crear el sobreturno");
            setStep("form");
            return;
          }
        } else {
          setBusy(false);
          setMsg(j.error ?? "No se pudo crear");
          setStep("form");
          return;
        }
      }
      // UX #3: avisar si la sincronización con Google falló (el turno igual quedó guardado).
      const data = (await r.json().catch(() => ({}))) as { googleSync?: { ok: boolean; error?: string } };
      setBusy(false);
      if (isOverbook) toast.success("Sobreturno agendado.");
      if (data.googleSync && !data.googleSync.ok) {
        toast.error("El turno se guardó, pero no se pudo sincronizar con Google Calendar. Revisá la conexión.");
      }
      onCreated();
      onClose();
    }
  }

  const isEdit = Boolean(editAppointment);
  const modalTitle = step === "confirm"
    ? (isEdit ? "Confirmá los cambios" : "Confirmá el turno")
    : (isEdit ? "Editar turno" : "Nuevo turno");

  // --- confirm step ---
  if (step === "confirm") {
    return (
      <Modal open={open} onClose={onClose} title={modalTitle} size="lg">
        <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>
          <div
            className={css({
              display: "flex",
              flexDir: "column",
              gap: "4",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              rounded: "xl",
              p: "4",
              bg: "#f8fafc",
            })}
          >
            <div>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "0.5" })}>Cliente</p>
              <p className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>{clientName}</p>
              <p className={css({ fontSize: "sm", color: "#475569" })}>{clientEmail || "Sin email"}</p>
              {clientPhone && <p className={css({ fontSize: "sm", color: "#475569" })}>{clientPhone}</p>}
            </div>
            <div
              className={css({ h: "1px", bg: "#e2e8f0" })}
            />
            <div>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "0.5" })}>Servicio</p>
              <p className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>
                {selected?.name ?? "—"}
                <span className={css({ fontWeight: "normal", color: "#64748b" })}>
                  {" "}· {effectiveDuration} min{selected?.price != null ? ` · $${selected.price}` : ""}
                </span>
              </p>
            </div>
            <div>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "0.5" })}>Fecha y hora</p>
              <p className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>{confirmDateLabel}</p>
            </div>
            {staffMemberId && (() => {
              const sel = staffOptions.find((s) => s.id === staffMemberId);
              return sel ? (
                <div>
                  <p className={css({ fontSize: "xs", color: "#64748b", mb: "0.5" })}>Profesional</p>
                  <p className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>{sel.name}</p>
                </div>
              ) : null;
            })()}
          </div>

          {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c" })}>{msg}</p> : null}

          <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end", flexWrap: "wrap" })}>
            <Button
              variant="secondary"
              onClick={() => { setStep("form"); setMsg(null); }}
              disabled={busy}
            >
              Volver a editar
            </Button>
            <Button variant="primary" disabled={busy} onClick={() => void submit()}>
              {busy ? "Guardando..." : isEdit ? "Confirmar cambios" : "Confirmar y crear turno"}
            </Button>
          </div>
        </div>
      </Modal>
    );
  }

  // --- form step ---
  return (
    <Modal open={open} onClose={onClose} title={modalTitle} size="3xl">
      {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "3" })}>{msg}</p> : null}
      <div className={css({ display: "flex", flexDir: "column", gap: "5" })}>

        {/* Servicio */}
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Servicio</span>
          <select
            className={css({
              h: "12",
              px: "4",
              fontSize: "md",
              color: "#0f172a",
              borderWidth: "1px",
              borderColor: "#dbe2ea",
              rounded: "lg",
              bg: "#f8fafc",
              width: "100%",
              _focus: { outline: "none", bg: "white", borderColor: "#334155" },
            })}
            value={serviceId}
            onChange={(e) => {
              setServiceId(e.target.value);
              setSlotIso(null);
              setSlots([]);
            }}
          >
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} · {s.duration} min{s.price != null ? ` · $${s.price}` : ""}
              </option>
            ))}
          </select>
          {selected?.requiresDeposit ? (
            <p className={css({ fontSize: "xs", color: "#b45309", mt: "1" })}>
              Nota: este servicio tiene seña configurada para reservas públicas (en turnos internos queda como "sin cobro").
            </p>
          ) : null}
        </label>

        {/* Duración */}
        {selected?.kind === "court" ? (
          <p className={css({ fontSize: "xs", color: "#64748b" })}>
            Duración fija de la cancha: <strong>{selected.duration} min</strong>
          </p>
        ) : (
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>
            Duración (minutos)
            {selected ? (
              <span className={css({ color: "#94a3b8", ml: "1" })}>
                — predeterminada: {selected.duration} min
              </span>
            ) : null}
          </span>
          <div className={css({ display: "flex", alignItems: "center", gap: "3" })}>
            <input
              type="number"
              min={5}
              max={480}
              step={5}
              value={customDurationMin}
              onChange={(e) => setCustomDurationMin(e.target.value === "" ? "" : Number(e.target.value))}
              placeholder={String(selected?.duration ?? 30)}
              className={css({
                h: "12",
                px: "4",
                w: "36",
                fontSize: "md",
                color: "#0f172a",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                rounded: "lg",
                bg: "#f8fafc",
                _focus: { outline: "none", bg: "white", borderColor: "#334155" },
              })}
            />
            {selected && (selected.durationOptions?.length ?? 0) > 0 && (
              <div className={css({ display: "flex", gap: "1.5", flexWrap: "wrap" })}>
                {selected.durationOptions!.map((d) => (
                  <button
                    key={d}
                    type="button"
                    onClick={() => setCustomDurationMin(d)}
                    className={css({
                      px: "2.5",
                      py: "1",
                      rounded: "full",
                      fontSize: "xs",
                      fontWeight: "medium",
                      borderWidth: "1px",
                      cursor: "pointer",
                      borderColor: customDurationMin === d ? "#4f46e5" : "#dbe2ea",
                      bg: customDurationMin === d ? "#eef2ff" : "white",
                      color: customDurationMin === d ? "#4f46e5" : "#475569",
                    })}
                  >
                    {d} min
                  </button>
                ))}
              </div>
            )}
          </div>
        </label>
        )}

        {/* Staff */}
        {staffOptions.length > 0 ? (
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Asignar a</span>
            <select
              className={css({
                h: "12",
                px: "4",
                fontSize: "md",
                color: "#0f172a",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                rounded: "lg",
                bg: "#f8fafc",
                width: "100%",
              })}
              value={staffMemberId}
              onChange={(e) => setStaffMemberId(e.target.value)}
            >
              {staffOptions.length > 1 ? <option value="">Sin asignar</option> : null}
              {staffOptions.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
            {staffMemberId ? (() => {
              const sel = staffOptions.find((s) => s.id === staffMemberId);
              const dotColor = sel?.googleCalendarBgColor ?? googleEventColorHex(sel?.googleColorId);
              const calLabel = sel?.googleCalendarId
                ? `El turno se creará en la agenda "${sel.name}" de Google.`
                : `Color en Google: ${GOOGLE_EVENT_COLORS.find((c) => c.id === sel?.googleColorId)?.label ?? "Color"}`;
              return (
                <span className={css({ display: "inline-flex", alignItems: "center", gap: "2", fontSize: "xs", color: "#64748b" })}>
                  <span className={css({ w: "3", h: "3", rounded: "full" })} style={{ background: dotColor }} />
                  {calLabel}
                </span>
              );
            })() : null}
          </label>
        ) : null}

        {/* Fecha y hora */}
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          <div>
            <h3 className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>
              Elegí fecha y horario
            </h3>
            <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
              Solo podés elegir días con turnos libres según tu agenda.
            </p>
          </div>

          <div
            className={css({
              display: "flex",
              flexDir: { base: "column", md: "row" },
              gap: { base: "4", md: "6" },
              alignItems: { md: "flex-start" },
            })}
          >
            <div className={css({ w: { base: "100%", md: "auto" } })}>
              <BookCalendarGrid
                calendarMonth={calendarMonth}
                onPrevMonth={goPrevMonth}
                onNextMonth={goNextMonth}
                canPrevMonth={canPrevMonth}
                canNextMonth={canNextMonth}
                timeZone={tz}
                primaryColor={PRIMARY}
                minDateStr={minDate}
                availableDates={availableDates}
                selectedDate={dateStr}
                onSelectDate={(d) => {
                  setDateStr(d);
                  setSlotIso(null);
                }}
                loadingMonth={loadingMonth}
              />
              {!loadingMonth && availableDates.size === 0 ? (
                <p className={css({ fontSize: "xs", color: "#64748b", mt: "2", lineHeight: "1.4" })}>
                  No hay días con turnos libres este mes. Probá con las flechas.
                </p>
              ) : null}
            </div>

            <div className={css({ flex: "1", minW: 0, w: "100%" })}>
              <p className={css({ fontWeight: "semibold", mb: "2", fontSize: "sm", color: "#0f172a" })}>
                Horarios disponibles
              </p>
              {!dateStr ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>
                  Tocá un día resaltado en el calendario para ver los horarios.
                </p>
              ) : loadingSlots ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>Cargando…</p>
              ) : slots.length === 0 ? (
                <p className={css({ fontSize: "sm", color: "#64748b" })}>
                  No hay horarios para esa fecha. Probá otro día.
                </p>
              ) : (
                <div
                  className={css({
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
                    gap: "2",
                  })}
                >
                  {slots.map((s) => (
                    <button
                      key={s.iso}
                      type="button"
                      onClick={() => {
                        setSlotIso(s.iso);
                        setCustomTime("");
                      }}
                      className={css({
                        py: "2.5",
                        px: "2",
                        rounded: "lg",
                        borderWidth: "2px",
                        cursor: "pointer",
                        fontSize: "sm",
                        fontWeight: "semibold",
                        bg: "white",
                      })}
                      style={{
                        borderColor: slotIso === s.iso && !customTime ? PRIMARY : "#e2e8f0",
                        color: slotIso === s.iso && !customTime ? PRIMARY : "#0f172a",
                        backgroundColor: slotIso === s.iso && !customTime ? "#eef2ff" : "white",
                      }}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}

              <div className={css({ mt: "4" })}>
                <p className={css({ fontWeight: "semibold", mb: "1", fontSize: "sm", color: "#0f172a" })}>
                  Hora personalizada
                </p>
                <p className={css({ fontSize: "xs", color: "#64748b", mb: "2" })}>
                  Escribí una hora libre (HH:mm). Si la completás, ignora el slot del grid.
                </p>
                <input
                  type="text"
                  placeholder="Ej: 09:00"
                  value={customTime}
                  maxLength={5}
                  onChange={(e) => {
                    setCustomTime(e.target.value);
                    if (e.target.value) setSlotIso(null);
                  }}
                  className={css({
                    h: "10",
                    px: "3",
                    w: "28",
                    fontSize: "md",
                    color: "#0f172a",
                    borderWidth: "1px",
                    borderColor: customTime && !customTimeValid ? "#b91c1c" : "#dbe2ea",
                    rounded: "lg",
                    bg: "#f8fafc",
                    _focus: { outline: "none", bg: "white", borderColor: "#334155" },
                  })}
                />
                {customTime && !customTimeValid && (
                  <p className={css({ fontSize: "xs", color: "#b91c1c", mt: "1" })}>
                    Formato inválido. Usá HH:mm (ej: 09:00).
                  </p>
                )}
                {customTime && customTimeValid && (
                  <p className={css({ fontSize: "xs", color: "#16a34a", mt: "1" })}>
                    Se usará {customTime} como hora de inicio.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Datos del cliente */}
        <div className={css({ display: "flex", flexDir: "column", gap: "3" })}>
          <h3 className={css({ fontSize: "md", fontWeight: "semibold", color: "#0f172a" })}>
            Datos del cliente
          </h3>

          {/* Autocomplete */}
          <ClientSearchAutocomplete clients={clients} onSelect={selectClient} />

          {selectedClientId && (
            <div
              className={css({
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "3",
                px: "3",
                py: "1.5",
                bg: "#eef2ff",
                rounded: "lg",
                borderWidth: "1px",
                borderColor: "#c7d2fe",
              })}
            >
              <div className={css({ display: "flex", alignItems: "center", gap: "3", flex: 1, minW: 0 })}>
                <span className={css({ fontSize: "xs", color: "#4338ca", flexShrink: 0 })}>
                  Cliente del CRM seleccionado
                </span>
                <button
                  type="button"
                  onClick={() => {
                    onClose();
                    navigate(`/dashboard/odontogram/by-client/${selectedClientId}`);
                  }}
                  className={css({
                    fontSize: "xs",
                    fontWeight: "semibold",
                    color: "#4338ca",
                    bg: "rgba(99,102,241,0.12)",
                    px: "2",
                    py: "0.5",
                    rounded: "md",
                    border: "none",
                    cursor: "pointer",
                    flexShrink: 0,
                    _hover: { bg: "rgba(99,102,241,0.2)" },
                  })}
                >
                  Historia clínica
                </button>
              </div>
              <button
                type="button"
                onClick={clearClientSelection}
                className={css({ fontSize: "xs", color: "#6366f1", cursor: "pointer", flexShrink: 0, _hover: { color: "#4338ca" } })}
              >
                Cambiar
              </button>
            </div>
          )}

          {/* Nombre */}
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} />
          </label>

          {/* Email + Phone */}
          <div className={css({ display: "grid", gridTemplateColumns: { base: "1fr", md: "1fr 1fr" }, gap: "3" })}>
            <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <span className={css({ fontSize: "xs", color: "#64748b" })}>
                  Email{!clientPhone.trim() && <span style={{ color: "#b91c1c" }}> *</span>}
                </span>
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => {
                    setClientEmail(e.target.value);
                    if (emailError) setEmailError(null);
                  }}
                  placeholder="cliente@email.com"
                  className={emailError ? css({ borderColor: "#b91c1c !important" }) : undefined}
                />
              </label>
              {emailError && (
                <p className={css({ fontSize: "xs", color: "#b91c1c" })}>{emailError}</p>
              )}
            </div>

            <div className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
                <span className={css({ fontSize: "xs", color: "#64748b" })}>
                  Teléfono WhatsApp{!clientEmail.trim() && <span style={{ color: "#b91c1c" }}> *</span>}
                </span>
                <Input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => {
                    setClientPhone(e.target.value);
                    if (phoneError) setPhoneError(null);
                  }}
                  placeholder="Ej: 3794556599"
                  className={phoneError ? css({ borderColor: "#b91c1c !important" }) : undefined}
                />
              </label>
              {phoneError && (
                <p className={css({ fontSize: "xs", color: "#b91c1c" })}>{phoneError}</p>
              )}
            </div>
          </div>
          <span className={css({ fontSize: "10px", color: "#94a3b8", lineHeight: "1.4" })}>
            Completá al menos uno (email o teléfono). El teléfono se usa para notificaciones por WhatsApp.
          </span>

          {/* Título original de Google (solo si el turno vino de convertir un evento) */}
          {isEdit && originalGoogleTitle != null ? (
            <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
              <span className={css({ fontSize: "xs", color: "#64748b" })}>
                Título original en Google Calendar
              </span>
              <textarea
                className={css({
                  px: "4",
                  py: "3",
                  fontSize: "md",
                  color: "#0f172a",
                  borderWidth: "1px",
                  borderColor: "#dbe2ea",
                  rounded: "lg",
                  bg: "#f8fafc",
                  width: "100%",
                  minH: "12",
                  resize: "vertical",
                  _placeholder: { color: "#94a3b8" },
                  _focus: { outline: "none", bg: "white", borderColor: "#334155" },
                })}
                value={originalGoogleTitle}
                onChange={(e) => setOriginalGoogleTitle(e.target.value)}
              />
              <span className={css({ fontSize: "10px", color: "#94a3b8", lineHeight: "1.4" })}>
                Capturado al convertir el evento de Google en turno. Editalo acá si querés corregirlo;
                los cambios de título hechos directo en Google se ignoran.
              </span>
            </label>
          ) : null}

          {/* Notas */}
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Notas internas</span>
            <textarea
              className={css({
                px: "4",
                py: "3",
                fontSize: "md",
                color: "#0f172a",
                borderWidth: "1px",
                borderColor: "#dbe2ea",
                rounded: "lg",
                bg: "#f8fafc",
                width: "100%",
                minH: "16",
                resize: "vertical",
                _placeholder: { color: "#94a3b8" },
                _focus: { outline: "none", bg: "white", borderColor: "#334155" },
              })}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </label>
        </div>

        {/* Actions */}
        <div className={css({ display: "flex", gap: "2", flexWrap: "wrap", justifyContent: "flex-end" })}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={!canProceedToConfirm} onClick={handleProceedToConfirm}>
            {isEdit ? "Revisar cambios" : "Revisar y confirmar"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
